import _ from "lodash";
import type { Annotation } from "@rapid/types/annotation";
import type { PromptLabel } from "@rapid/store/slices/modelIntentSlice";
import type { InferenceStrategyConfig } from "./types";
import { SAM3_INFERENCE_CONFIDENCE_THRESHOLD } from "@rapid/utils/predictions";
import { separateLabelsByDiffStatus } from "@rapid/hooks/useAutoPredictions/utils";

/**
 * Inference strategy for the Build flow.
 *
 * Build flow uses mixed inference based on hasDiffs:
 * - Labels WITHOUT diffs (no user edits) -> SAM3 text prompting
 * - Labels WITH diffs (user has drawn/edited boxes) -> OWLv2 box prompting
 *
 * Box prompting uses annotations from ALL files to find similar objects.
 */
export class BuildFlowStrategy implements InferenceStrategyConfig {
    getSam3Labels(promptLabels: Map<string, PromptLabel>, textPrompts: string[]): string[] {
        const { labelsWithoutDiffs } = separateLabelsByDiffStatus(promptLabels);
        // Include new text prompts that aren't in promptLabels yet
        const existingLabelNames = new Set(promptLabels.keys());
        const newTextPrompts = textPrompts.filter((prompt) => !existingLabelNames.has(prompt));
        return _.uniq([...labelsWithoutDiffs, ...newTextPrompts]);
    }

    getOwlv2Labels(promptLabels: Map<string, PromptLabel>): string[] {
        const { labelsWithDiffs } = separateLabelsByDiffStatus(promptLabels);
        return labelsWithDiffs;
    }

    shouldAppendPredictions(isMixedInference: boolean): boolean {
        // In Build flow, only append when running mixed inference
        // (both SAM3 and OWLv2 running in parallel)
        return isMixedInference;
    }

    getBoxPromptingAnnotations(
        allAnnotationsForLabel: Annotation[],
        _currentFileAnnotations: Annotation[]
    ): Annotation[] {
        // Build flow uses ALL annotations across all files
        return allAnnotationsForLabel;
    }

    shouldIncludeImageUrls(): boolean {
        // Build flow uses storage lookup with signed URLs
        return false;
    }

    getTextPromptConfidenceThreshold(): number {
        return SAM3_INFERENCE_CONFIDENCE_THRESHOLD;
    }

    hasDiffsRelevant(): boolean {
        return true;
    }
}
